import React, { useContext, Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import OfflineIndicator from './components/OfflineIndicator';
import InstallPrompt from './components/InstallPrompt';
import { AuthContext } from './context/AuthContext';
import { ThemeProvider } from './hooks/useTheme';
import { BadgesProvider } from './contexts/BadgesContext';
import { HeaderProvider } from './context/HeaderContext';
import useBadgeSync from './hooks/useBadgeSync';

// Cada página es su propio chunk (React.lazy) en vez de ir todas en el bundle
// principal: antes cualquier carga del CRM bajaba y compilaba TODO el código
// de TODAS las pantallas de una sola vez (~2.4MB sin comprimir), sin importar
// a cuál se entrara. Pega fuerte en hardware/navegadores viejos (parseo y
// compilación de JS es lo más caro ahí). Con lazy, cada ruta trae solo lo suyo.

// Auth
const Login = lazy(() => import('./pages/auth/Login'));

// CRM (todos los usuarios)
const CRMDashboard = lazy(() => import('./pages/crm/Dashboard'));
const CRMContacts = lazy(() => import('./pages/crm/Contacts'));
const CRMContactDetail = lazy(() => import('./pages/crm/ContactDetail'));
const CRMPipeline = lazy(() => import('./pages/crm/Pipeline'));
const SalesPipeline = lazy(() => import('./pages/crm/SalesPipeline'));
const SalesMyDay = lazy(() => import('./pages/crm/SalesMyDay'));
const OpportunityDetail = lazy(() => import('./pages/crm/OpportunityDetail'));
const CRMConversations = lazy(() => import('./pages/crm/Conversations'));
const BandejaSinAsignar = lazy(() => import('./pages/crm/BandejaSinAsignar'));
const RendimientoEquipo = lazy(() => import('./pages/crm/RendimientoEquipo'));
const CRMSeguimientos = lazy(() => import('./pages/crm/Seguimientos'));
const CRMClientesRiesgo = lazy(() => import('./pages/crm/ClientesRiesgo'));
const CRMBonificaciones = lazy(() => import('./pages/crm/Bonificaciones'));
const CRMDistribuidores = lazy(() => import('./pages/crm/Distribuidores'));
const CRMAvisos = lazy(() => import('./pages/crm/Avisos'));
const CRMConocimientoBot = lazy(() => import('./pages/crm/ConocimientoBot'));
const CRMKnowledgeBase = lazy(() => import('./pages/crm/KnowledgeBase'));
const CRMOpportunityNew = lazy(() => import('./pages/crm/OpportunityNew'));
const BotResponses = lazy(() => import('./pages/crm/BotResponses'));
const BotEscalations = lazy(() => import('./pages/crm/BotEscalations'));
const BotDevIssues = lazy(() => import('./pages/crm/BotDevIssues'));
const DevPipeline = lazy(() => import('./pages/crm/DevPipeline'));
const BotAnalytics = lazy(() => import('./pages/crm/BotAnalytics'));
const SupportKitchen = lazy(() => import('./pages/crm/SupportKitchen'));
const MonitorEquipo = lazy(() => import('./pages/crm/MonitorEquipo'));
const CustomerAnalysis = lazy(() => import('./pages/soporte/CustomerAnalysis'));
const Registros = lazy(() => import('./pages/soporte/Registros'));
const SoporteRemoto = lazy(() => import('./pages/soporte/SoporteRemoto'));
const ParaProbar = lazy(() => import('./pages/soporte/ParaProbar'));

// Casos v2 (reemplazan a los Tickets v1)
const Casos = lazy(() => import('./pages/casos/Casos'));
// Agenda de demos
const Calendario = lazy(() => import('./pages/atencion/Calendario'));
const CasoDetail = lazy(() => import('./pages/casos/CasoDetail'));

// Ficha 360 de Cliente
const FichaCliente = lazy(() => import('./pages/clientes/FichaCliente'));

// Asistentes IA internos
const AsistenteSoporte = lazy(() => import('./pages/asistente/AsistenteSoporte'));
const AsistenteComercial = lazy(() => import('./pages/asistente/AsistenteComercial'));

// Admin only
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const AdminSettings = lazy(() => import('./pages/admin/Settings'));
const BotSettings = lazy(() => import('./pages/admin/BotSettings'));
const AppsSettings = lazy(() => import('./pages/admin/AppsSettings'));
const SupportSchedule = lazy(() => import('./pages/admin/SupportSchedule'));
const Performance = lazy(() => import('./pages/admin/Performance'));
const MonitorSistema = lazy(() => import('./pages/admin/MonitorSistema'));
const Intervenciones = lazy(() => import('./pages/admin/Intervenciones'));
const TurnosInforme = lazy(() => import('./pages/admin/TurnosInforme'));
const MisIntervenciones = lazy(() => import('./pages/crm/MisIntervenciones'));
const QualityControl = lazy(() => import('./pages/admin/QualityControl'));
const ModelosIA = lazy(() => import('./pages/admin/ModelosIA'));
const Partners = lazy(() => import('./pages/admin/Partners'));
const HubFranquicias = lazy(() => import('./pages/admin/HubFranquicias'));
const Marketing = lazy(() => import('./pages/admin/Marketing'));
const Negocio = lazy(() => import('./pages/admin/Negocio'));
const Proyeccion = lazy(() => import('./pages/admin/Proyeccion'));

// Fallback de Suspense: mismo spinner que ya usaban ProtectedRoute/AdminRoute
// mientras resuelve la sesión, para que la espera se vea igual en ambos casos.
const RouteLoader = () => (
  <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

// Configurar React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useContext(AuthContext);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

// Admin only route wrapper
const AdminRoute = ({ children }) => {
  const { user, loading } = useContext(AuthContext);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user || user.role !== 'admin') {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
};

// Ruta gateada por lista de roles (mismo patrón que AdminRoute/SupervisorRoute)
const RoleRoute = ({ roles, children }) => {
  const { user, loading } = useContext(AuthContext);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user || !roles.includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// SupervisorRoute disponible para rutas que requieren admin o supervisor
const SupervisorRoute = ({ children }) => {
  const { user, loading } = useContext(AuthContext);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user || !['admin', 'supervisor'].includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
};

// Red de seguridad global: un crash de render en cualquier página muestra un
// error recuperable en lugar de dejar toda la app en blanco.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info?.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-6">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow p-6 text-center">
            <div className="text-4xl mb-3" aria-hidden="true">😵</div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Algo se rompió en esta pantalla
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 break-words">
              {String(this.state.error?.message || this.state.error).slice(0, 200)}
            </p>
            <div className="flex justify-center gap-2">
              <button
                onClick={() => { this.setState({ error: null }); window.location.href = '/dashboard'; }}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Ir al inicio
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 rounded-lg bg-primary text-white text-sm hover:opacity-90"
              >
                Recargar
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  // Mantener el badge del PWA siempre fresco: sync inicial, en focus, y
  // periodicSync (best-effort) como red de seguridad.
  useBadgeSync();
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <HeaderProvider>
        <BadgesProvider>
        <ErrorBoundary>
        <Suspense fallback={<RouteLoader />}>
        <Routes>
          {/* Auth */}
          <Route path="/login" element={<Login />} />
          
          {/* Redirect root to dashboard */}
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <Navigate to="/dashboard" />
              </ProtectedRoute>
            } 
          />
          
          {/* CRM - Todos los usuarios autenticados */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <CRMDashboard />
              </ProtectedRoute>
            } 
          />
          
          <Route
            path="/casos"
            element={
              <RoleRoute roles={['admin', 'supervisor', 'soporte', 'soporte_ventas', 'comercial', 'dev']}>
                <Casos />
              </RoleRoute>
            }
          />

          <Route
            path="/calendario"
            element={
              <RoleRoute roles={['admin', 'supervisor', 'soporte', 'soporte_ventas', 'comercial', 'dev']}>
                <Calendario />
              </RoleRoute>
            }
          />

          <Route
            path="/casos/:id"
            element={
              <RoleRoute roles={['admin', 'supervisor', 'soporte', 'soporte_ventas', 'comercial', 'dev']}>
                <CasoDetail />
              </RoleRoute>
            }
          />
          <Route
            path="/clientes"
            element={
              <RoleRoute roles={['admin', 'supervisor', 'soporte', 'soporte_ventas', 'comercial', 'dev']}>
                <FichaCliente />
              </RoleRoute>
            }
          />

          <Route
            path="/clientes/:termino"
            element={
              <RoleRoute roles={['admin', 'supervisor', 'soporte', 'soporte_ventas', 'comercial', 'dev']}>
                <FichaCliente />
              </RoleRoute>
            }
          />
          <Route
            path="/bandeja-sin-asignar"
            element={
              <ProtectedRoute>
                <BandejaSinAsignar />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/conversations" 
            element={
              <ProtectedRoute>
                <CRMConversations />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/contacts" 
            element={
              <ProtectedRoute>
                <CRMContacts />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/contacts/:id" 
            element={
              <ProtectedRoute>
                <CRMContactDetail />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/pipeline" 
            element={
              <ProtectedRoute>
                <CRMPipeline />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/ventas/mi-dia"
            element={
              <ProtectedRoute>
                <SalesMyDay />
              </ProtectedRoute>
            } 
          />
          
          <Route
            path="/ventas/pipeline"
            element={
              <ProtectedRoute>
                <SalesPipeline />
              </ProtectedRoute>
            } 
          />
          
          <Route
            path="/ventas/oportunidad/:id"
            element={
              <ProtectedRoute>
                <OpportunityDetail />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/opportunities/new" 
            element={
              <ProtectedRoute>
                <CRMOpportunityNew />
              </ProtectedRoute>
            } 
          />
          
          <Route
            path="/rendimiento-equipo"
            element={
              <ProtectedRoute>
                <RendimientoEquipo />
              </ProtectedRoute>
            }
          />
          {/* /analytics y /operator-metrics medían sobre datos de la era WATI
              (is_resolved / assigned_to / updated_at) y no coincidían entre sí:
              quedan como redirect a la página única. */}
          <Route path="/analytics" element={<Navigate to="/rendimiento-equipo" replace />} />
          <Route path="/operator-metrics" element={<Navigate to="/rendimiento-equipo" replace />} />
          <Route 
            path="/seguimientos" 
            element={
              <ProtectedRoute>
                <CRMSeguimientos />
              </ProtectedRoute>
            } 
          />
          
          <Route
            path="/avisos"
            element={
              <RoleRoute roles={['admin', 'supervisor', 'soporte', 'soporte_ventas']}>
                <CRMAvisos />
              </RoleRoute>
            }
          />

          <Route
            path="/distribuidores"
            element={
              <RoleRoute roles={['admin', 'supervisor', 'soporte', 'soporte_ventas', 'comercial', 'dev']}>
                <CRMDistribuidores />
              </RoleRoute>
            }
          />

          <Route
            path="/bonificaciones"
            element={
              <RoleRoute roles={['admin', 'supervisor', 'soporte', 'soporte_ventas']}>
                <CRMBonificaciones />
              </RoleRoute>
            }
          />

          <Route
            path="/conocimiento-bot"
            element={
              <RoleRoute roles={['admin', 'supervisor', 'soporte', 'soporte_ventas']}>
                <CRMConocimientoBot />
              </RoleRoute>
            }
          />

          <Route 
            path="/clientes-riesgo" 
            element={
              <ProtectedRoute>
                <CRMClientesRiesgo />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/knowledge-base" 
            element={
              <ProtectedRoute>
                <CRMKnowledgeBase />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/bot-responses" 
            element={
              <ProtectedRoute>
                <BotResponses />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/bot-escalations" 
            element={
              <ProtectedRoute>
                <BotEscalations />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/bot-dev-issues" 
            element={
              <ProtectedRoute>
                <BotDevIssues />
              </ProtectedRoute>
            } 
          />
          
          <Route
            path="/dev-pipeline"
            element={
              <AdminRoute>
                <DevPipeline />
              </AdminRoute>
            }
          />
          <Route 
            path="/bot-analytics" 
            element={
              <ProtectedRoute>
                <BotAnalytics />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/soporte/clientes"
            element={
              <ProtectedRoute>
                <CustomerAnalysis />
              </ProtectedRoute>
            }
          />

          {/* Asistentes IA internos */}
          <Route
            path="/asistente-soporte"
            element={
              <RoleRoute roles={['admin', 'supervisor', 'soporte', 'soporte_ventas', 'dev']}>
                <AsistenteSoporte />
              </RoleRoute>
            }
          />
          <Route
            path="/asistente-comercial"
            element={
              <RoleRoute roles={['admin', 'comercial']}>
                <AsistenteComercial />
              </RoleRoute>
            }
          />

          {/* Admin only */}
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <AdminUsers />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/modelos-ia"
            element={
              <AdminRoute>
                <ModelosIA />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/partners"
            element={
              <AdminRoute>
                <Partners />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/hub-franquicias"
            element={
              <AdminRoute>
                <HubFranquicias />
              </AdminRoute>
            }
          />
          <Route
            path="/marketing"
            element={
              <RoleRoute roles={['admin', 'supervisor', 'comercial']}>
                <Marketing />
              </RoleRoute>
            }
          />
          <Route
            path="/negocio"
            element={
              <RoleRoute roles={['admin']}>
                <Negocio />
              </RoleRoute>
            }
          />
          <Route
            path="/negocio/proyeccion"
            element={
              <RoleRoute roles={['admin']}>
                <Proyeccion />
              </RoleRoute>
            }
          />
          
          <Route 
            path="/admin/settings" 
            element={
              <AdminRoute>
                <AdminSettings />
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/bot-settings" 
            element={
              <AdminRoute>
                <BotSettings />
              </AdminRoute>
            } 
          />
          <Route
            path="/admin/apps-settings"
            element={
              <AdminRoute>
                <AppsSettings />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/support-schedule"
            element={
              <AdminRoute>
                <SupportSchedule />
              </AdminRoute>
            }
          />
          
          <Route
            path="/admin/performance"
            element={
              <AdminRoute>
                <Performance />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/intervenciones"
            element={
              <RoleRoute roles={['admin', 'supervisor']}>
                <Intervenciones />
              </RoleRoute>
            }
          />
          <Route
            path="/admin/turnos-informe"
            element={
              <RoleRoute roles={['admin', 'supervisor']}>
                <TurnosInforme />
              </RoleRoute>
            }
          />
          <Route
            path="/mis-intervenciones"
            element={
              <RoleRoute roles={['admin', 'supervisor', 'soporte', 'soporte_ventas']}>
                <MisIntervenciones />
              </RoleRoute>
            }
          />
          <Route
            path="/admin/monitor-sistema"
            element={
              <AdminRoute>
                <MonitorSistema />
              </AdminRoute>
            }
          />
          <Route
            path="/calidad"
            element={
              <AdminRoute>
                <QualityControl />
              </AdminRoute>
            }
          />
          {/* Panel Cocina Soporte */}
          <Route path="/monitor-soporte" element={<ProtectedRoute><SupportKitchen /></ProtectedRoute>} />
          <Route path="/monitor-equipo" element={<SupervisorRoute><MonitorEquipo /></SupervisorRoute>} />
          <Route path="/registros" element={<ProtectedRoute><Registros /></ProtectedRoute>} />
          <Route
            path="/soporte-remoto"
            element={
              <RoleRoute roles={['admin', 'supervisor', 'soporte', 'soporte_ventas']}>
                <SoporteRemoto />
              </RoleRoute>
            }
          />
          <Route
            path="/para-probar"
            element={
              <RoleRoute roles={['admin', 'supervisor', 'soporte', 'soporte_ventas', 'dev']}>
                <ParaProbar />
              </RoleRoute>
            }
          />
          {/* Legacy redirects (por si alguien tiene bookmarks viejos) */}
          <Route path="/crm" element={<Navigate to="/dashboard" />} />
          <Route path="/crm/*" element={<Navigate to="/dashboard" />} />
          <Route path="/wati" element={<Navigate to="/dashboard" />} />
          <Route path="/wati/*" element={<Navigate to="/dashboard" />} />
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
        </BadgesProvider>
        </HeaderProvider>
        
        <Toaster
          position="bottom-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              style: {
                background: '#21A250',
              },
            },
            error: {
              style: {
                background: '#EB4D50',
              },
            },
          }}
        />
      <OfflineIndicator />
      <InstallPrompt />
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
